import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { storeFilterActions } from '../../modules/Actions';

export const newFilterID = 'NEW_FILTER_ID';
export interface FilterResponse {
  filterID: number | string;
  filterName: string;
  ordcstmCdList: string[];
  sortOrder: number;
}

export interface FilterItem extends FilterResponse {
  isActive: boolean;
  isExist?: boolean;
  isChecked?: boolean;
  code?: string;
  checked?: boolean;
  filterUuid?: string;
}

export interface FilterState {
  isLoading: boolean;
  isError: boolean;
  isDraft: boolean;
  isDraftOrder: boolean;
  data: FilterItem[];
  cloneData: FilterItem[];
  msg: ResultMessageDto[];
}

export const initialState: FilterState = {
  isLoading: true,
  isError: false,
  isDraft: false,
  data: [],
  cloneData: [],
  msg: [],
  isDraftOrder: false,
};

const setLoadingState = (state: FilterState): FilterState => {
  return {
    ...state,
    isLoading: true,
  };
};

const setFailErrorMsg = (state: FilterState, payload: ResultMessageDto[]): FilterState => {
  return {
    ...state,
    isLoading: false,
    msg: payload,
  };
};

export const FilterReducer = reducerWithInitialState(initialState)
  .case(storeFilterActions.getFilterSetting.start, (state: FilterState) => {
    return setLoadingState(state);
  })
  .case(storeFilterActions.getFilterSetting.end, (state: FilterState, payload: FilterItem[]) => {
    let dataConvert = payload;
    if (state.data.length) {
      const checkFilterIds = state.data
        .filter((filterItem: FilterItem) => filterItem?.checked)
        .map((filterItem: FilterItem) => filterItem.filterID);
      dataConvert = payload.map((itemNew: FilterItem) => {
        return {
          ...itemNew,
          checked: checkFilterIds.includes(itemNew.filterID),
        };
      });
    }

    return {
      ...state,
      isLoading: false,
      isDraft: false,
      data: dataConvert,
      cloneData: dataConvert,
    };
  })
  .case(storeFilterActions.getFilterSetting.fail, (state: FilterState, payload: ResultMessageDto[]) => {
    return {
      ...state,
      isLoading: false,
      isError: true,
      msg: payload,
    };
  })
  .case(storeFilterActions.createFilterSetting.start, (state: FilterState) => {
    return setLoadingState(state);
  })
  .case(storeFilterActions.createFilterSetting.fail, (state: FilterState, payload: ResultMessageDto[]) => {
    return setFailErrorMsg(state, payload);
  })
  .case(storeFilterActions.updateFilterSetting.start, (state: FilterState) => {
    return setLoadingState(state);
  })
  .case(storeFilterActions.updateFilterSetting.end, (state: FilterState) => {
    return {
      ...state,
      isLoading: false,
      isDraft: false,
      cloneData: state.data,
      isDraftOrder: false,
    };
  })
  .case(storeFilterActions.updateFilterSetting.fail, (state: FilterState, payload: ResultMessageDto[]) => {
    return setFailErrorMsg(state, payload);
  })
  .case(storeFilterActions.updateFilterSetting.exist, (state: FilterState, payload: FilterItem) => {
    return {
      ...state,
      data: state.data.map((item) => ({ ...item, isExist: item.filterID === payload.filterID ? true : item.isExist })),
    };
  })
  .case(storeFilterActions.deleteFilter.start, (state: FilterState) => {
    return setLoadingState(state);
  })
  .case(storeFilterActions.deleteFilter.end, (state: FilterState, payload: FilterItem) => {
    const result = state.data
      .filter((item) => item.filterID !== payload.filterID)
      .map((item, index) => ({ ...item, isActive: index === 0 }));

    return {
      ...state,
      isLoading: false,
      isDraft: false,
      data: result,
      cloneData: result,
    };
  })
  .case(storeFilterActions.deleteFilter.fail, (state: FilterState, payload: ResultMessageDto[]) => {
    return setFailErrorMsg(state, payload);
  })
  .case(storeFilterActions.applyCheckboxFilter, (state: FilterState, payload: FilterItem) => {
    return {
      ...state,
      data: state.data.map((item) => (item.filterID === payload.filterID ? { ...item, checked: !item.checked } : item)),
    };
  })
  .case(storeFilterActions.activeFilter, (state: FilterState, payload: FilterItem) => {
    return {
      ...state,
      isDraft: false,
      data: state.data.map((item) => ({ ...item, isActive: item.filterID === payload.filterID })),
    };
  })
  .case(storeFilterActions.deleteErr, (state: FilterState, payload: string) => {
    return {
      ...state,
      msg: state.msg.filter((item) => item.resultMessageCd != payload),
    };
  })
  .case(storeFilterActions.resetErr, (state: FilterState) => {
    return {
      ...state,
      msg: [],
    };
  })
  .case(storeFilterActions.draftFilter.create, (state: FilterState) => {
    const defaultFilter: FilterItem = {
      filterName: '',
      filterID: newFilterID,
      ordcstmCdList: [],
      isActive: true,
      isExist: true,
      sortOrder: 0,
      filterUuid: Math.random().toString(36).substring(2, 15),
    };
    return {
      ...state,
      isDraft: true,
      data: [defaultFilter, ...state.data.map((item) => ({ ...item, isActive: false }))],
    };
  })
  .case(storeFilterActions.draftFilter.update, (state: FilterState, payload: FilterItem) => {
    const results =
      state.data.length > 0
        ? state.data.map((item) => (item.filterID === payload.filterID ? { ...payload, isExist: true } : item))
        : [{ ...payload, isExist: true }];
    return {
      ...state,
      isDraft: true,
      data: results,
    };
  })
  .case(storeFilterActions.draftFilter.rollBack, (state: FilterState) => {
    return {
      ...state,
      data: state.cloneData,
      isDraft: false,
      isDraftOrder: false,
      isError: false,
    };
  })
  .case(storeFilterActions.draftFilter.updateOrder, (state: FilterState, payload: FilterItem[]) => {
    // フィルタの並び順が元と同じかどうかを判定
    const sameOrder =
      payload.length === state.cloneData.length && payload.every((item, index) => item.filterID === state.cloneData[index].filterID);

    return {
      ...state,
      data: payload,
      // 並び順同じ = false, 並び順違う = true
      isDraftOrder: !sameOrder,
    };
  })
  .case(storeFilterActions.draftFilter.cloneCheckbox, (state: FilterState) => {
    return {
      ...state,
      cloneData: state.data,
    };
  });
